import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CITIES, PROJECT_TYPES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", project_type: "Deck", city: "", message: "" };
const field = "w-full border-0 border-b border-white/15 bg-transparent px-0 py-4 text-base text-neutral-50 placeholder:text-neutral-500 focus:border-white focus:outline-none focus:ring-0 transition-colors";

export const QuoteForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.city) return toast.error("Please choose your city.");
    setBusy(true);
    try {
      await axios.post(`${API}/quotes`, f);
      setDone(true);
      toast.success("Request received. We'll be in touch within one business day.");
      setF(EMPTY);
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Please check your details and try again." : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="quote-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex h-full flex-col items-start justify-center border border-white/[0.08] bg-[#121212] p-10">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#0A0A0A]"><Check className="h-6 w-6" /></span>
        <h3 className="mt-8 font-display text-3xl font-bold tracking-tight text-neutral-50">Thanks! Your request is in.</h3>
        <p className="mt-4 max-w-sm text-neutral-400">A Newland project lead will reach out within one business day to set up your site walk.</p>
        <button data-testid="quote-send-another" onClick={() => setDone(false)} className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-white hover:text-white">Send another request</button>
      </motion.div>
    );
  }

  return (
    <form data-testid="quote-lead-form" onSubmit={submit} className="border border-white/[0.08] bg-[#121212] p-6 md:p-10">
      <div className="eyebrow mb-4">Project type</div>
      <div className="flex flex-wrap gap-2">
        {PROJECT_TYPES.map((t) => (
          <button type="button" key={t} data-testid={`project-type-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} onClick={() => setF({ ...f, project_type: t })}
            className={`relative rounded-full border px-4 py-2 text-sm transition-colors ${f.project_type === t ? "border-white text-[#0A0A0A]" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>
            <AnimatePresence>{f.project_type === t && <motion.span layoutId="pt-pill" className="absolute inset-0 -z-0 rounded-full bg-white" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}</AnimatePresence>
            <span className="relative">{t}</span>
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-x-8 md:grid-cols-2">
        <input data-testid="input-full-name" required minLength={2} placeholder="Full name" value={f.name} onChange={set("name")} className={field} />
        <input data-testid="input-phone-number" required minLength={7} type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} className={field} />
        <input data-testid="input-email-address" required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} />
        <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
          <SelectTrigger data-testid="select-tri-cities-location" className="h-auto rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-4 text-base text-neutral-50 focus:ring-0 data-[placeholder]:text-neutral-500">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#121212] text-neutral-100">
            {[...CITIES, "Other nearby"].map((c) => (
              <SelectItem key={c} value={c} data-testid={`city-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <textarea data-testid="input-project-details" rows={3} maxLength={2000} placeholder="Tell us about your project: size, materials, timeline..." value={f.message} onChange={set("message")} className={`${field} mt-2 resize-none`} />
      <button data-testid="submit-quote-request-button" disabled={busy} type="submit"
        className="group mt-10 flex w-full items-center justify-between rounded-full bg-white py-4 pl-8 pr-4 font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200 disabled:opacity-60">
        {busy ? "Sending..." : "Request my free estimate"}
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0A0A0A] text-white transition-transform duration-500 group-hover:rotate-45">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
        </span>
      </button>
    </form>
  );
};
